import { useEffect, useState } from "react";
import { Alert, Box, Button, CircularProgress, TextField, Typography } from "@mui/material";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import Navbar from "scenes/widgets/navbar";
import { setLogin } from "state";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";

const VerifyEmailPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("loading");
  const [message, setMessage] = useState("E-Mail wird verifiziert...");
  const [email, setEmail] = useState("");
  const [resendFeedback, setResendFeedback] = useState(null);
  const [resendPreviewUrl, setResendPreviewUrl] = useState("");
  const [isResending, setIsResending] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);

  useEffect(() => {
    let redirectTimeoutId;
    const token = searchParams.get("token");
    if (!token) {
      setStatus("error");
      setMessage("Verifizierungslink ist ungueltig.");
      return;
    }

    const verify = async () => {
      try {
        const response = await fetch(
          `${BASE_URL}/auth/verify-email?token=${encodeURIComponent(token)}`
        );
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.message || "Verifizierung fehlgeschlagen.");
        }
        if (data?.token && data?.user) {
          dispatch(
            setLogin({
              user: data.user,
              token: data.token,
            })
          );
          setStatus("success");
          setMessage("E-Mail erfolgreich verifiziert. Du wirst weitergeleitet…");
          setIsRedirecting(true);
          redirectTimeoutId = setTimeout(() => {
            navigate("/wettbewerb", { replace: true });
          }, 3000);
          return;
        }
        setStatus("success");
        setMessage(data.message || "E-Mail erfolgreich verifiziert.");
      } catch (error) {
        setStatus("error");
        setMessage(error.message || "Verifizierung fehlgeschlagen.");
      }
    };

    verify();

    return () => {
      if (redirectTimeoutId) {
        clearTimeout(redirectTimeoutId);
      }
    };
  }, [dispatch, navigate, searchParams]);

  const handleResend = async () => {
    setResendFeedback(null);
    setIsResending(true);
    try {
      const response = await fetch(`${BASE_URL}/auth/resend-verification`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Verifizierungs-E-Mail konnte nicht gesendet werden.");
      }
      const emailSent = data?.emailSent !== false;
      setResendFeedback({
        severity: emailSent ? "success" : "warning",
        text:
          data.message ||
          "Falls ein unverifiziertes Konto existiert, wurde eine neue Verifizierungs-E-Mail versendet.",
      });
      setResendPreviewUrl(data?.verificationPreviewUrl || "");
    } catch (error) {
      setResendFeedback({
        severity: "error",
        text: error.message || "Verifizierungs-E-Mail konnte nicht gesendet werden.",
      });
      setResendPreviewUrl("");
    } finally {
      setIsResending(false);
    }
  };

  return (
    <Box>
      <Navbar />
      <Box maxWidth="560px" mx="auto" mt="2rem" p="2rem">
        <Typography variant="h4" mb="1rem">
          E-Mail Verifizierung
        </Typography>
        {status === "loading" && (
          <Box display="flex" alignItems="center" gap="0.75rem">
            <CircularProgress size={22} />
            <Typography>{message}</Typography>
          </Box>
        )}
        {status !== "loading" && (
          <Alert severity={status === "success" ? "success" : "error"} sx={{ mb: "1rem" }}>
            {message}
          </Alert>
        )}
        {status === "error" && (
          <Box mb="1rem">
            <Typography variant="body2" mb="0.5rem">
              Link abgelaufen oder ungueltig? Hier kannst du eine neue Verifizierungs-E-Mail anfordern.
            </Typography>
            {resendFeedback && (
              <Alert severity={resendFeedback.severity} sx={{ mb: "0.75rem" }}>
                {resendFeedback.text}
              </Alert>
            )}
            {resendPreviewUrl && (
              <Alert severity="info" sx={{ mb: "0.75rem" }}>
                Test-Link (nur lokal): {resendPreviewUrl}
              </Alert>
            )}
            <Box display="flex" gap="0.5rem" flexWrap="wrap">
              <TextField
                label="E-Mail"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                size="small"
              />
              <Button
                variant="outlined"
                onClick={handleResend}
                disabled={!email || isResending}
              >
                {isResending ? "Wird gesendet..." : "Neu senden"}
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default VerifyEmailPage;

